import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, X, Zap, Crown, Rocket } from "lucide-react";

const PricingValue = () => {
  const [selectedPlan, setSelectedPlan] = useState('professional');
  const navigate = useNavigate();

  const plans = [
    {
      id: 'starter',
      name: 'Starter AI System',
      icon: Zap,
      popular: false,
      price: '$4,997',
      originalPrice: '$9,997',
      savings: 'Save $5,000',
      downPayment: '$1,999',
      balance: '$2,998',
      maintenance: '$500',
      description: 'Perfect for small businesses ready to automate',
      features: [
        '3 AI Agents (Lead Gen + Qualification + Demo)',
        'Up to 500 leads processed monthly',
        'Basic reporting and analytics',
        'Email integration',
        '30-day implementation',
        'Comprehensive training videos library',
        'Phone & email support',
        '90-day satisfaction guarantee'
      ],
      notIncluded: [
        'Advanced AI agents (3 additional agents)',
        'Live training sessions',
        'Train-the-trainer programs',
        'Custom integrations',
        'Priority support',
        'Dedicated account manager'
      ],
      cta: 'Start with Starter Plan',
      color: 'var(--accent-blue-200)',
      accentColor: 'var(--accent-blue-400)'
    },
    {
      id: 'professional',
      name: 'Professional AI Suite',
      icon: Crown,
      popular: true,
      price: '$12,997',
      originalPrice: '$24,997',
      savings: 'Save $12,000 (MOST POPULAR)',
      downPayment: '$5,199',
      balance: '$7,798',
      maintenance: '$1,200',
      description: 'Complete AI transformation for growing businesses',
      features: [
        'All 6 AI Agents (Complete System)',
        'Unlimited lead processing',
        'Advanced analytics & ROI tracking',
        'Full CRM integration',
        'Custom workflows & automations', 
        'API integrations with existing tools',
        '20-day priority implementation',
        '1 full day training session',
        '2-hour train-the-trainer session',
        'Dedicated account manager',
        'White-glove onboarding',
        '1-year warranty & optimization',
        'Satisfaction guarantee'
      ],
      notIncluded: [
        'Enterprise-grade security',
        'Custom AI model training',
        'Extended training programs (3+ days)',
        'Multi-location deployment'
      ],
      cta: 'Get Complete AI System',
      color: 'var(--accent-orange-200)',
      accentColor: 'var(--accent-orange-400)'
    },
    {
      id: 'enterprise',
      name: 'Enterprise AI Platform',
      icon: Rocket,
      popular: false,
      price: '$29,997',
      originalPrice: '$59,997',
      savings: 'Save $30,000',
      downPayment: '$11,999',
      balance: '$17,998',
      maintenance: '$2,500',
      description: 'Enterprise-grade AI for scaling organizations',
      features: [
        'Everything in Professional',
        'Custom AI model development',
        'Enterprise security & compliance',
        'Multi-location deployment',
        'Advanced integrations (SAP, Oracle, etc.)',
        'Custom reporting dashboards',
        '10-day priority implementation',
        'Up to 3 days of on-site training sessions',
        '4-hour comprehensive train-the-trainer program',
        'Dedicated implementation team',
        '24/7 priority support',
        'Quarterly optimization reviews',
        'Unlimited warranty & upgrades'
      ],
      notIncluded: [],
      cta: 'Transform Enterprise-Wide',
      color: 'var(--accent-purple-200)',
      accentColor: 'var(--accent-purple-400)'
    }
  ];

  return (
    <section className="py-20" style={{ background: 'var(--gradient-hero-subtle)' }}>
      <div className="container">
        <div className="text-center mb-16">
          <div className="hero-announcement mb-6">
            <Zap size={12} style={{ color: 'var(--accent-orange-400)' }} />
            <span>LAUNCH PRICING: 50% OFF ALL PLANS</span>
          </div>
          <h2 className="heading-1 mb-4">
            Choose the Right <span style={{ color: 'var(--accent-orange-400)' }}>AI System</span> for Your Business
          </h2>
          <p className="body-large mb-6" style={{ 
            maxWidth: '700px', 
            margin: '0 auto',
            color: 'var(--text-secondary)' 
          }}>
            Special founding client pricing - 50% off all implementation tiers. Lock in these rates now.
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-8 max-w-7xl mx-auto mb-16">
          {plans.map((plan) => {
            const IconComponent = plan.icon;
            const isSelected = selectedPlan === plan.id;
            
            return (
              <div 
                key={plan.id}
                className={`voice-card hover-lift cursor-pointer relative ${
                  plan.popular ? 'border-2' : ''
                }`}
                style={{ 
                  background: plan.color,
                  borderColor: plan.popular ? plan.accentColor : 'transparent',
                  transform: plan.popular ? 'scale(1.05)' : 'scale(1)'
                }}
                onClick={() => setSelectedPlan(plan.id)}
              >
                {plan.popular && (
                  <div 
                    className="absolute -top-4 left-1/2 transform -translate-x-1/2 px-4 py-2 rounded-full text-white font-semibold text-xs"
                    style={{ background: plan.accentColor }}
                  >
                    MOST POPULAR - BEST VALUE
                  </div>
                )}

                <div className="text-center mb-6">
                  <IconComponent size={32} className="mx-auto mb-3" style={{ color: plan.accentColor }} />
                  <h3 className="voice-card-title mb-2">{plan.name}</h3>
                  <p className="body-small mb-4" style={{ color: 'var(--text-secondary)' }}>
                    {plan.description}
                  </p>
                  
                  <div className="mb-4">
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <span className="heading-1" style={{ color: plan.accentColor }}>
                        {plan.price}
                      </span>
                      <span className="body-small line-through" style={{ color: 'var(--text-muted)' }}>
                        {plan.originalPrice}
                      </span>
                    </div>
                    <div className="body-small font-semibold mb-2" style={{ color: 'var(--accent-green-200)' }}>
                      {plan.savings}
                    </div>
                    
                    {/* Payment Structure */}
                    <div className="p-3 rounded-lg mb-3" style={{ background: 'rgba(255, 255, 255, 0.7)' }}>
                      <div className="caption mb-2 font-semibold">Payment Structure:</div>
                      <div className="flex justify-between items-center mb-1">
                        <span className="caption">Down Payment (40%):</span>
                        <span className="caption font-semibold">{plan.downPayment}</span>
                      </div>
                      <div className="flex justify-between items-center mb-2">
                        <span className="caption">Balance (completion):</span>
                        <span className="caption font-semibold">{plan.balance}</span>
                      </div>
                      <div className="border-t pt-2" style={{ borderColor: 'var(--border-light)' }}>
                        <div className="flex justify-between items-center">
                          <span className="caption">Monthly Maintenance:</span>
                          <span className="caption font-semibold" style={{ color: plan.accentColor }}>
                            {plan.maintenance}/mo
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Features */}
                <div className="mb-6">
                  <div className="body-small font-semibold mb-3">✓ What's Included:</div>
                  <ul className="space-y-2">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <Check size={16} className="flex-shrink-0 mt-1" style={{ color: 'var(--accent-green-200)' }} />
                        <span className="caption">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  {plan.notIncluded.length > 0 && (
                    <>
                      <div className="body-small font-semibold mb-3 mt-4">✗ Not Included:</div>
                      <ul className="space-y-2">
                        {plan.notIncluded.map((feature, index) => (
                          <li key={index} className="flex items-start gap-2">
                            <X size={16} className="flex-shrink-0 mt-1" style={{ color: '#DC2626' }} />
                            <span className="caption" style={{ color: 'var(--text-muted)' }}>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                </div>

                <button 
                  className={`w-full ${plan.popular ? 'btn-primary' : 'btn-secondary'} hover-scale`}
                >
                  {plan.cta}
                </button>
              </div>
            );
          })}
        </div>

        {/* Final CTA */}
        <div className="text-center">
          <div className="inline-flex flex-col items-center gap-4 p-8 rounded-lg max-w-2xl mx-auto" style={{ 
            background: 'var(--bg-card)',
            border: '2px solid var(--accent-orange-400)'
          }}>
            <h3 className="heading-2 mb-2">
              Ready to Automate Your Business?
            </h3>
            <p className="body-medium mb-4" style={{ color: 'var(--text-secondary)' }}>
              Choose the plan that fits your business needs and start your AI transformation.
            </p>
            <button 
              className="btn-primary hover-scale text-lg px-8 py-4"
              onClick={() => navigate(`/checkout?plan=${selectedPlan}`)}
            >
              Get Started Now
            </button>
            <div className="flex items-center gap-6 mt-4">
              <div className="caption">✓ 30-Day Implementation</div>
              <div className="caption">✓ Satisfaction Guarantee</div>
              <div className="caption">✓ Professional Support</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PricingValue;